import type { BookingStatus } from '@massilia/prisma-generated'
import type { BookingDetail } from '@massilia/queries'

import {
  useDownloadMyBookingConfirmationPdfMutation,
  useDownloadMyBookingInvoicePdfMutation,
  useDownloadMyBookingReservationContractPdfMutation,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { Download, FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface BookingSummaryCardProps {
  booking: BookingDetail
}

export function BookingSummaryCard({ booking }: BookingSummaryCardProps) {
  const title = getStatusTitle(booking.status)
  const downloadPdfMutation = useDownloadMyBookingConfirmationPdfMutation()
  const downloadInvoiceMutation = useDownloadMyBookingInvoicePdfMutation()
  const downloadContractMutation = useDownloadMyBookingReservationContractPdfMutation()
  const isTicketIssued = booking.passengers.some((p) => p.ticketNumber != null)

  const handleDownloadPdf = async () => {
    try {
      const blob = await downloadPdfMutation.mutateAsync(booking.id)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch {
      toast.error('Erreur lors du téléchargement du document')
    }
  }

  const handleDownloadInvoice = async () => {
    try {
      const blob = await downloadInvoiceMutation.mutateAsync(booking.id)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch {
      toast.error('Erreur lors du téléchargement de la facture')
    }
  }

  const handleDownloadContract = async () => {
    try {
      const blob = await downloadContractMutation.mutateAsync(booking.id)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch {
      toast.error('Erreur lors du téléchargement du contrat')
    }
  }

  return (
    <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
      <div className="text-gray-light-900 text-lg font-semibold sm:text-2xl">{title}</div>

      <SummaryRow label="Numéro de dossier" value={booking.pnr ?? booking.reference} />
      <SummaryRow label="Adresse email" value={booking.client.email ?? undefined} />
      <SummaryRow label="Téléphone" value={booking.client.phoneNumbers?.[0]?.number ?? undefined} />
      <SummaryRow label="Tarif" value={formatPrice(centsToEuros(booking.totalPrice))} />

      <div className="mt-2 flex flex-wrap gap-3">
        <Button
          className="text-xs sm:text-sm"
          disabled={!isTicketIssued}
          onClick={handleDownloadPdf}
          title={isTicketIssued ? undefined : "Le billet n'est pas encore émis"}
          type="button"
        >
          {downloadPdfMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Download className="size-4" />
          )}
          Télécharger le billet
        </Button>
        <Button className="text-xs sm:text-sm" onClick={handleDownloadInvoice} type="button">
          {downloadInvoiceMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FileText className="size-4" />
          )}
          Télécharger la facture
        </Button>
        <Button className="text-xs sm:text-sm" onClick={handleDownloadContract} type="button">
          {downloadContractMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FileText className="size-4" />
          )}
          Télécharger le contrat
        </Button>
        <Button className="text-xs sm:text-sm" type="button" variant="outline">
          <FileText className="size-4" />
          Conditions générales de ventes
        </Button>
      </div>
    </div>
  )
}

function getStatusTitle(status: BookingStatus): string {
  switch (status) {
    case 'AWAITING_DEPOSIT':
      return 'Votre réservation est en attente de règlement'
    case 'CANCELLED':
      return 'Ce dossier a été annulé'
    case 'DEPOSIT_PAID':
      return 'Votre réservation est confirmée !'
    case 'FULLY_PAID_PENDING':
    case 'FULLY_PAID_TRAVELED':
      return 'Votre réservation est finalisée !'
    default:
      return 'Votre réservation'
  }
}
